/**
 * Flow editor keybindings — config + matcher.
 *
 * Every keyboard action is an id with a default combo string. The store holds
 * the user's overrides (persisted); FlowApp's single keydown dispatcher matches
 * the live event against the bindings and runs the action. The Keys settings
 * tab lets the user rebind each one (capture a keypress) and reset to defaults.
 *
 * Combo format (canonical): modifiers in fixed order then the key, joined by
 * '+'. e.g. "Ctrl+S", "Alt+Space", "ArrowUp", "Delete", "1", "T".
 */

export type FlowAction =
  | 'playPause' | 'playFromStart'
  | 'selectAll' | 'deselect' | 'copy' | 'cut' | 'paste' | 'pasteMirror' | 'delete' | 'undo' | 'redo'
  | 'nudgeLater' | 'nudgeEarlier' | 'nudgeLaneLeft' | 'nudgeLaneRight'
  | 'brushTap' | 'brushArrowL' | 'brushArrowC' | 'brushArrowR' | 'brushTrail'
  | 'bookmarkToggle' | 'bookmarkNext' | 'bookmarkPrev'
  | 'playTest' | 'playTestScored' | 'editWindow'
  | 'stepForward' | 'stepBack' | 'snapFiner' | 'snapCoarser'
  | 'laneKey1' | 'laneKey2' | 'laneKey3' | 'laneKey4' | 'laneKey5'
  | 'newMap' | 'open' | 'save';

export const DEFAULT_KEYBINDINGS: Record<FlowAction, string> = {
  playPause:     'Space',
  playFromStart: 'Alt+Space',
  selectAll:     'Ctrl+A',
  deselect:      'Ctrl+D',
  copy:          'Ctrl+C',
  cut:           'Ctrl+X',
  paste:         'Ctrl+V',
  pasteMirror:   'Alt+V',
  delete:        'Delete',
  undo:          'Ctrl+Z',
  redo:          'Ctrl+Y',
  nudgeLater:    'ArrowUp',
  nudgeEarlier:  'ArrowDown',
  nudgeLaneLeft: 'ArrowLeft',
  nudgeLaneRight:'ArrowRight',
  brushTap:      '1',
  brushArrowL:   '2',
  brushArrowC:   '3',
  brushArrowR:   '4',
  brushTrail:    'T',
  bookmarkToggle:'B',
  bookmarkNext:  ']',
  bookmarkPrev:  '[',
  playTest:       'Shift+Space',        // practice — no score, scrub freely
  playTestScored: 'Ctrl+Shift+Space',   // scored run — from 0:00, no going back
  // Reviewer ask: Ctrl+E toggles the lock directly, no settings trip needed.
  editWindow:     'Ctrl+E',
  // SR-editor parity (reviewer ask): W/S walk the timeline, A/D change the
  // step division. These share keys with the lane keys ON PURPOSE — lane keys
  // only act while the music is running (live mapping / play test), and these
  // four only act while it's paused, so the two sets never collide.
  stepForward:    'W',
  stepBack:       'S',
  snapFiner:      'D',
  snapCoarser:    'A',
  // Lane keys — shared by Live Mapping (record) and Play Test (hit). Lane 5
  // only fires on 5-lane difficulties.
  laneKey1:      'A',
  laneKey2:      'S',
  laneKey3:      'D',
  laneKey4:      'F',
  laneKey5:      'G',
  // Alt+N, not the desktop-style Ctrl+N: Chrome hard-reserves Ctrl+N for a new
  // window (a page can't preventDefault it), so the old default never fired.
  newMap:        'Alt+N',
  open:          'Ctrl+O',
  save:          'Ctrl+S',
};

export interface BindableAction { action: FlowAction; label: string; description: string; }

export const KEYBIND_SECTIONS: { title: string; items: BindableAction[] }[] = [
  { title: 'Playback', items: [
    { action: 'playPause',     label: 'Play / Pause',      description: 'Start or stop playback from the current position.' },
    { action: 'playFromStart', label: 'Play from start',   description: 'Jump to the beginning and play.' },
    { action: 'playTest',       label: 'Practice Play',     description: 'Play the map in-editor with the lane keys — no score, and you can scrub back/forward and keep playing.' },
    { action: 'playTestScored', label: 'Scored Run',        description: 'Full play-through with hit/miss scoring: starts from the beginning, no rewinding until you exit.' },
  ]},
  { title: 'Lane Keys (Live Mapping & Play Test)', items: [
    { action: 'laneKey1', label: 'Lane 1', description: 'Hit / record lane 1.' },
    { action: 'laneKey2', label: 'Lane 2', description: 'Hit / record lane 2.' },
    { action: 'laneKey3', label: 'Lane 3', description: 'Hit / record lane 3.' },
    { action: 'laneKey4', label: 'Lane 4', description: 'Hit / record lane 4.' },
    { action: 'laneKey5', label: 'Lane 5', description: 'Hit / record lane 5 (5-lane difficulties).' },
  ]},
  { title: 'Selection & Editing', items: [
    { action: 'selectAll',     label: 'Select all',        description: 'Select every note in the chart.' },
    { action: 'deselect',      label: 'Deselect',          description: 'Clear the selection (and any pending cut).' },
    { action: 'copy',          label: 'Copy',              description: 'Copy the selected notes.' },
    { action: 'cut',           label: 'Cut',               description: 'Cut the selected notes (paste drops them at the playhead).' },
    { action: 'paste',         label: 'Paste',             description: 'Paste copied/cut notes at the playhead.' },
    { action: 'pasteMirror',   label: 'Mirror paste',      description: 'Paste at the playhead flipped across the centre line (lanes + flick directions mirrored).' },
    { action: 'delete',        label: 'Delete selection',  description: 'Delete the selected notes.' },
    { action: 'undo',          label: 'Undo',              description: 'Undo the last change.' },
    { action: 'redo',          label: 'Redo',              description: 'Bring back the change you just undid.' },
    { action: 'nudgeLater',    label: 'Nudge later',       description: 'Move the selection one snap step later in time.' },
    { action: 'nudgeEarlier',  label: 'Nudge earlier',     description: 'Move the selection one snap step earlier in time.' },
    { action: 'nudgeLaneLeft', label: 'Move lane left',    description: 'Move the selected notes one lane to the left.' },
    { action: 'nudgeLaneRight',label: 'Move lane right',   description: 'Move the selected notes one lane to the right.' },
  ]},
  { title: 'Note Brush', items: [
    { action: 'brushTap',      label: 'Tap brush',         description: 'Switch the brush to plain tap notes.' },
    { action: 'brushArrowL',   label: 'Arrow-Left brush',  description: 'Switch the brush to left flick notes.' },
    { action: 'brushArrowC',   label: 'Arrow-Up brush',    description: 'Switch the brush to up/center flick notes.' },
    { action: 'brushArrowR',   label: 'Arrow-Right brush', description: 'Switch the brush to right flick notes.' },
    { action: 'brushTrail',    label: 'Trail brush',       description: 'Switch the brush to hold/trail notes.' },
  ]},
  { title: 'Editing Safety', items: [
    { action: 'editWindow', label: 'Edit-window lock', description: 'Toggle "edit only near the playhead" — clicks far from the playhead are ignored so scrubbing can\'t nuke distant notes.' },
  ]},
  { title: 'Timeline & Snap (paused only)', items: [
    { action: 'stepForward', label: 'Step forward',  description: 'Move the playhead one snap step later (paused only).' },
    { action: 'stepBack',    label: 'Step back',     description: 'Move the playhead one snap step earlier (paused only).' },
    { action: 'snapFiner',   label: 'Finer snap',    description: 'Halve the snap step (paused only; D like the SR editor).' },
    { action: 'snapCoarser', label: 'Coarser snap',  description: 'Double the snap step (paused only; A like the SR editor).' },
  ]},
  { title: 'Bookmarks', items: [
    { action: 'bookmarkToggle', label: 'Toggle bookmark',    description: 'Drop a bookmark at the playhead, or remove the one already there.' },
    { action: 'bookmarkNext',   label: 'Next bookmark',      description: 'Jump to the next bookmark after the playhead.' },
    { action: 'bookmarkPrev',   label: 'Previous bookmark',  description: 'Jump back to the bookmark before the playhead.' },
  ]},
  { title: 'File', items: [
    { action: 'newMap',        label: 'New map',           description: 'Start a new beatmap.' },
    { action: 'open',          label: 'Open library',      description: 'Open the saved-charts library.' },
    { action: 'save',          label: 'Save',              description: 'Save the current beatmap.' },
  ]},
];

/** Mouse / wheel actions — fixed reference, not rebindable. */
export const MOUSE_REFERENCE: { keys: string; desc: string }[] = [
  { keys: 'Click floor',     desc: 'Place a note on the lane at the current snap.' },
  { keys: 'Click note',      desc: 'Delete that note.' },
  { keys: 'Alt + drag note', desc: 'Move a note (lane + time).' },
  { keys: 'Wheel',           desc: 'Scrub the playhead.' },
  { keys: 'Ctrl + Wheel',    desc: 'Change the snap division.' },
  { keys: 'Right-drag',      desc: 'Box-select notes on the highway.' },
  { keys: 'Esc',             desc: 'Clear the selection / cancel a pending cut.' },
];

/** Canonical combo string for a KeyboardEvent. */
export function comboFromEvent(e: KeyboardEvent): string {
  const parts: string[] = [];
  if (e.ctrlKey || e.metaKey) parts.push('Ctrl');
  if (e.altKey) parts.push('Alt');
  if (e.shiftKey) parts.push('Shift');
  let key = e.key;
  if (e.code === 'Space' || key === ' ') key = 'Space';
  else if (key === 'Backspace') key = 'Delete';
  else if (key.length === 1) key = key.toUpperCase();
  parts.push(key);
  return parts.join('+');
}

/** True for a bare modifier press (Ctrl/Alt/Shift/Meta) — ignored when capturing. */
export function isModifierOnly(e: KeyboardEvent): boolean {
  return e.key === 'Control' || e.key === 'Alt' || e.key === 'Shift' || e.key === 'Meta';
}

/** Find the action bound to this event, or null. Shift acts as a transparent
 *  extra modifier for un-Ctrl'd bindings (so Shift+Arrow still nudges, ×4). */
export function matchFlowAction(e: KeyboardEvent, bindings: Record<string, string>): FlowAction | null {
  const combo = comboFromEvent(e);
  for (const a in bindings) if (bindings[a] === combo) return a as FlowAction;
  if (e.shiftKey && !e.ctrlKey && !e.metaKey && !e.altKey) {
    const noShift = combo.replace(/^Shift\+/, '');
    for (const a in bindings) if (bindings[a] === noShift) return a as FlowAction;
  }
  return null;
}

/** lowercase `KeyboardEvent.key` → lane index, built from the user's laneKey1..5
 *  bindings. Combos with modifiers are skipped: lane keys are meant to be bare
 *  keys you can hammer. Shared by Live Mapping (record) and Play Test (hit). */
export function laneKeyMap(bindings: Record<string, string>): Record<string, number> {
  const out: Record<string, number> = {};
  for (let i = 0; i < 5; i++) {
    const id = `laneKey${i + 1}` as FlowAction;
    const combo = bindings[id] || DEFAULT_KEYBINDINGS[id];
    if (!combo || combo.includes('+')) continue;
    out[combo === 'Space' ? ' ' : combo.toLowerCase()] = i;
  }
  return out;
}

/** Pretty label for a combo (arrows → glyphs). */
export function prettyCombo(combo: string): string {
  return combo
    .replace('ArrowUp', '↑').replace('ArrowDown', '↓')
    .replace('ArrowLeft', '←').replace('ArrowRight', '→');
}
